import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Bookmark, Trash2, ChevronRight, DollarSign, MapPin,
  TrendingUp, Briefcase, Building, ExternalLink
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LuxuryBackground, Card, PageHero } from "../components/shared/UIComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = { deal: Briefcase, opportunity: TrendingUp, borrower: Building, lender: Building, investor: DollarSign };
const TYPE_COLORS = { deal: "bg-blue-50 text-blue-600", opportunity: "bg-emerald-50 text-emerald-600", borrower: "bg-amber-50 text-amber-600", lender: "bg-purple-50 text-purple-600", investor: "bg-gold/10 text-gold-dark" };

export default function WatchlistPage() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWatchlist = () => {
    setLoading(true);
    axios.get(`${API}/watchlist`, { headers })
      .then(r => setItems(r.data.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(fetchWatchlist, []);

  const removeItem = async (entityType, entityId) => {
    try {
      await axios.delete(`${API}/watchlist/${entityType}/${entityId}`, { headers });
      setItems(prev => prev.filter(i => !(i.entity_type === entityType && i.entity_id === entityId)));
    } catch {}
  };

  const filtered = filter === "all" ? items : items.filter(i => i.entity_type === filter);
  const types = [...new Set(items.map(i => i.entity_type))];

  return (
    <div className="relative" data-testid="watchlist-page">
      <PageHero eyebrow="Your Watchlist" title="Saved Entities"
        description="Track deals, opportunities, and entities you're interested in." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">

          {/* Filter tabs */}
          <div className="flex gap-2 mb-6 flex-wrap" data-testid="watchlist-filters">
            <button onClick={() => setFilter("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === "all" ? "bg-emerald-700 text-white" : "bg-white border border-cream-warm text-[#666] hover:border-emerald-200"}`}>
              All ({items.length})
            </button>
            {types.map(t => (
              <button key={t} onClick={() => setFilter(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all capitalize ${filter === t ? "bg-emerald-700 text-white" : "bg-white border border-cream-warm text-[#666] hover:border-emerald-200"}`}>
                {t}s ({items.filter(i => i.entity_type === t).length})
              </button>
            ))}
          </div>

          {/* Items */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : filtered.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12 text-[#999]">
                <Bookmark className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-lg mb-2">No saved items yet</p>
                <p className="text-sm">Bookmark deals and opportunities from the marketplace to track them here.</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {filtered.map((item, i) => (
                  <WatchlistItem key={`${item.entity_type}-${item.entity_id}`} item={item}
                    onRemove={() => removeItem(item.entity_type, item.entity_id)}
                    onNavigate={() => navigateToEntity(item.entity_type, item.entity_id)}
                    delay={i * 0.03} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function WatchlistItem({ item, onRemove, onNavigate, delay }) {
  const Icon = TYPE_ICONS[item.entity_type] || Briefcase;
  const colorClass = TYPE_COLORS[item.entity_type] || "bg-gray-50 text-gray-600";
  const preview = item.preview || {};
  const addedDate = item.created_at ? new Date(item.created_at).toLocaleDateString() : "";

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ delay }}
      data-testid={`watchlist-item-${item.entity_id}`}>
      <Card hover={true}>
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
            <Icon className="h-5 w-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#222] truncate">
                {item.entity_type === "deal" ? preview.property_address || `Deal ${(item.entity_id || "").slice(0, 8)}` :
                 item.entity_type === "opportunity" ? `${preview.loan_type || "Opportunity"} — ${preview.state || ""}` :
                 `${item.entity_type} ${(item.entity_id || "").slice(0, 8)}`}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium capitalize ${colorClass}`}>{item.entity_type}</span>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[#888]">
              {preview.stage && <span className="capitalize">{preview.stage.replace(/_/g, " ")}</span>}
              {preview.loan_type && <span>{preview.loan_type.replace(/_/g, " ")}</span>}
              {preview.target_raise && <span>{fmtUSD(preview.target_raise)} target</span>}
              {preview.projected_yield && <span>{preview.projected_yield}% yield</span>}
              {preview.requested_loan_amount && <span>{fmtUSD(preview.requested_loan_amount)}</span>}
              {addedDate && <span>Saved {addedDate}</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={onNavigate} data-testid={`watchlist-view-${item.entity_id}`}
              className="p-2 rounded-lg hover:bg-emerald-50 transition-colors text-emerald-600">
              <ExternalLink className="h-4 w-4" />
            </button>
            <button onClick={onRemove} data-testid={`watchlist-remove-${item.entity_id}`}
              className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-400 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
